import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { PAYMENT_NEW } from '../../constants/payment'
import appUserService from '../../services/appUserService'

function getOrderIdFromSearch(search) {
  const searchParams = new URLSearchParams(search)
  const value = searchParams.get('orderId')

  if (!value) {
    return null
  }

  const parsedValue = Number(value)
  return Number.isNaN(parsedValue) ? value : parsedValue
}

function parseJson(value, fallback = null) {
  if (!value || typeof value !== 'string') {
    return fallback
  }

  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

function getPlateListFromOrderItem(orderItem) {
  const orderItemVehicle = parseJson(orderItem?.orderItemVehicle, {})
  const vehicleIdentity = orderItemVehicle?.vehicleIdentity

  if (Array.isArray(vehicleIdentity)) {
    return vehicleIdentity.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean)
  }

  if (typeof vehicleIdentity === 'string' && vehicleIdentity.trim()) {
    return [vehicleIdentity.trim()]
  }

  const rawOrderItemValue = orderItem?.orderItemValue

  if (Array.isArray(rawOrderItemValue)) {
    return rawOrderItemValue.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean)
  }

  if (typeof rawOrderItemValue !== 'string') {
    return []
  }

  const trimmedValue = rawOrderItemValue.trim()
  if (!trimmedValue) {
    return []
  }

  const parsedOrderItemValue = parseJson(trimmedValue, trimmedValue)

  if (Array.isArray(parsedOrderItemValue)) {
    return parsedOrderItemValue.map((item) => (typeof item === 'string' ? item.trim() : '')).filter(Boolean)
  }

  if (typeof parsedOrderItemValue === 'string' && parsedOrderItemValue.trim()) {
    return [parsedOrderItemValue.trim()]
  }

  return [trimmedValue]
}

function getVehicleTypeFromOrderItem(orderItem) {
  const orderItemVehicle = parseJson(orderItem?.orderItemVehicle, {})
  const vehicleType = Number(orderItemVehicle?.vehicleType || orderItem?.vehicleType)

  return Number.isNaN(vehicleType) || vehicleType <= 0 ? undefined : vehicleType
}

function getProductVehicleTypeFromOrderItem(orderItem) {
  const vehicleType = Number(orderItem?.vehicleType)

  if (!Number.isNaN(vehicleType) && vehicleType > 0) {
    return vehicleType
  }

  return getVehicleTypeFromOrderItem(orderItem)
}

function buildVehiclesFromOrderDetail(orderDetail) {
  const orderItems = Array.isArray(orderDetail?.orderItems) ? orderDetail.orderItems : []
  const vehicleMap = new Map()

  orderItems.forEach((orderItem) => {
    const plateList = getPlateListFromOrderItem(orderItem)
    const vehicleType = getVehicleTypeFromOrderItem(orderItem)

    plateList.forEach((plate) => {
      const vehicleKey = `${plate}-${vehicleType || 'unknown'}`

      if (vehicleMap.has(vehicleKey)) {
        return
      }

      vehicleMap.set(vehicleKey, {
        plate,
        vehicleType
      })
    })
  })

  return [...vehicleMap.values()]
}

function buildProductFromOrderDetail(orderDetail) {
  const orderItems = Array.isArray(orderDetail?.orderItems) ? orderDetail.orderItems : []
  const orderItem = orderItems.find((item) => Number(item?.productId))

  if (!orderItem) {
    return null
  }

  const productPrice = Number(orderItem?.productPrice) || 0
  const productPriceAfterVAT = Number(orderItem?.payAmount) || Number(orderDetail?.totalAmount) || productPrice
  const productSize = orderItem?.productSize || String(orderItem?.quantity || 1)

  return {
    productId: orderItem?.productId,
    productName: orderItem?.productName || orderItem?.orderItemName,
    productBrand: orderItem?.productBrand,
    productProvider: orderItem?.productProvider,
    productType: orderItem?.productType,
    productCategory: orderItem?.productCategory,
    productDescription: orderItem?.productDescription,
    productSKU: orderItem?.productSKU,
    productPrice,
    productTax: orderItem?.productTax,
    usageDate: orderItem?.usageDate,
    productSize,
    vehicleType: getProductVehicleTypeFromOrderItem(orderItem),
    productPriceAfterVAT
  }
}

export default function useGetDetailOrderPayment() {
  const location = useLocation()
  const orderId = useMemo(() => getOrderIdFromSearch(location.search), [location.search])

  const [isLoading, setIsLoading] = useState(Boolean(orderId))
  const [orderDetail, setOrderDetail] = useState(null)

  useEffect(() => {
    let isMounted = true

    async function loadOrderDetail() {
      if (!orderId) {
        setOrderDetail(null)
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        const response = await appUserService.getOrderDetail({ id: orderId })

        if (!isMounted) {
          return
        }

        if (response?.statusCode === 200 && response?.data?.paymentStatus === PAYMENT_NEW) {
          setOrderDetail(response.data)
          return
        }

        setOrderDetail(null)
      } catch {
        if (isMounted) {
          setOrderDetail(null)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadOrderDetail()

    return () => {
      isMounted = false
    }
  }, [orderId])

  const vehicles = useMemo(() => buildVehiclesFromOrderDetail(orderDetail), [orderDetail])
  const product = useMemo(() => buildProductFromOrderDetail(orderDetail), [orderDetail])

  return {
    orderId,
    isLoading,
    orderDetail,
    vehicles,
    product,
    paymentMethodId: orderDetail?.paymentMethodId,
    paymentMethodType: orderDetail?.paymentMethodType
  }
}
